'use client';

import { useCallback, useRef, useState } from 'react';

const FADE_PX = 32;

interface ScrollEdgeFade {
  /** Attach to the horizontal scroller. */
  ref: (node: HTMLElement | null) => void;
  /** `mask-image` value, or undefined when nothing is clipped. */
  maskImage: string | undefined;
}

/**
 * Fades whichever edge of a horizontal scroller still hides content, so a scrollable
 * strip reads as scrollable instead of hard-cropped.
 *
 * Takes a callback ref rather than a RefObject: hosts like sheets and dialogs mount
 * their content lazily, so an effect keyed on a ref object would run once against a
 * null node and never measure anything.
 */
export function useScrollEdgeFade(fadePx: number = FADE_PX): ScrollEdgeFade {
  const [edges, setEdges] = useState({ left: false, right: false });
  const cleanupRef = useRef<(() => void) | null>(null);

  const ref = useCallback((node: HTMLElement | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;

    if (!node) {
      setEdges((prev) => (prev.left || prev.right ? { left: false, right: false } : prev));
      return;
    }

    const update = () => {
      const maxScroll = node.scrollWidth - node.clientWidth;
      const left = node.scrollLeft > 0;
      const right = node.scrollLeft < maxScroll - 1;
      // Same object when unchanged, so scrolling doesn't re-render on every frame.
      setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
    };

    update();
    node.addEventListener('scroll', update, { passive: true });
    // The scroller's own box and its content can each change without the other —
    // a late web font rewidens the labels while the scroller stays put.
    const ro = new ResizeObserver(update);
    ro.observe(node);
    if (node.firstElementChild) ro.observe(node.firstElementChild);

    cleanupRef.current = () => {
      node.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, []);

  let maskImage: string | undefined;
  if (edges.left && edges.right) {
    maskImage = `linear-gradient(to right, transparent, black ${fadePx}px, black calc(100% - ${fadePx}px), transparent)`;
  } else if (edges.right) {
    maskImage = `linear-gradient(to right, black calc(100% - ${fadePx}px), transparent)`;
  } else if (edges.left) {
    maskImage = `linear-gradient(to right, transparent, black ${fadePx}px, black 100%)`;
  }

  return { ref, maskImage };
}
